import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { formatPercent, formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { useVehicleMakeEfficiency } from '@/hooks/kpi/useVehicleMakeEfficiency';
import { useVehicleMakeModelEfficiency } from '@/hooks/kpi/useVehicleMakeModelEfficiency';

interface MakeModelPerformanceProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface MakeStats {
  make: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

interface ModelStats {
  model: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

export function MakeModelPerformance({ shopId, startDate, endDate, shopTimezone }: MakeModelPerformanceProps) {
  const [expandedMake, setExpandedMake] = useState<string | null>(null);

  // K-T-11: top-view aggregation sourced from canonical
  // `kpi_efficiency_by_vehicle_make` RPC.
  const { data: rawMakeRows, isLoading: loading } = useVehicleMakeEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
  });

  // K-T-12: per-model drill-down within the expanded make sourced from
  // canonical `kpi_efficiency_by_vehicle_make_model` RPC. Replaces the
  // prior local vehicles + tickets join and inline SUM/SUM math.
  // Behavior change: models with fewer than 3 qualifying tickets
  // (predicted IS NOT NULL AND actual IS NOT NULL AND actual > 0) now
  // render an "—" placeholder instead of a noisy single-row ratio,
  // matching K-T-6..K-T-11 convention.
  const { data: rawModelRows, isLoading: loadingDrilldown } = useVehicleMakeModelEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
    make: expandedMake,
  });

  const makeStats: MakeStats[] = (rawMakeRows ?? [])
    .map((r) => ({
      make: r.make,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      // Canonical RPC returns ratio as a fraction (e.g. 0.92);
      // the panel displays as a percent-rounded number to match the
      // pre-K-T-11 `calculateEfficiency` output convention.
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => b.ticketCount - a.ticketCount || a.make.localeCompare(b.make));

  const modelDrilldown: ModelStats[] = (rawModelRows ?? [])
    .map((r) => ({
      model: r.model,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => b.ticketCount - a.ticketCount);

  function handleMakeClick(make: string) {
    setExpandedMake(expandedMake === make ? null : make);
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Make & Model Performance</CardTitle>
        <CardDescription>
          Efficiency by vehicle make, click to drill down to models
        </CardDescription>
      </CardHeader>
      <CardContent>
        {makeStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No completed tickets in this date range</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Make</TableHead>
                <TableHead className="text-right">Tickets</TableHead>
                <TableHead className="text-right">Book Time</TableHead>
                <TableHead className="text-right">Actual Time</TableHead>
                <TableHead className="text-right">Efficiency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {makeStats.map((make) => (
                <>
                  <TableRow 
                    key={make.make}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleMakeClick(make.make)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expandedMake === make.make ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {make.make}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{make.ticketCount}</TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(make.totalBookMinutes)}</TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(make.totalActualMinutes)}</TableCell>
                    <TableCell className="text-right">
                      <Badge 
                        variant={make.efficiency && make.efficiency >= 100 ? 'default' : 'secondary'}
                        className={make.efficiency && make.efficiency >= 100 ? 'bg-green-500/20 text-green-700 dark:text-green-300' : ''}
                      >
                        {formatPercent(make.efficiency)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {expandedMake === make.make && (
                    <TableRow>
                      <TableCell colSpan={5} className="bg-muted/30 p-4">
                        {loadingDrilldown ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : modelDrilldown.length === 0 ? (
                          <p className="text-muted-foreground text-sm text-center">No model data</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Model</TableHead>
                                <TableHead className="text-right">Tickets</TableHead>
                                <TableHead className="text-right">Book</TableHead>
                                <TableHead className="text-right">Actual</TableHead>
                                <TableHead className="text-right">Efficiency</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {modelDrilldown.map((model) => (
                                <TableRow key={model.model}>
                                  <TableCell>{model.model}</TableCell>
                                  <TableCell className="text-right">{model.ticketCount}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(model.totalBookMinutes)}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(model.totalActualMinutes)}</TableCell>
                                  <TableCell className="text-right">
                                    <Badge variant="outline">{formatPercent(model.efficiency)}</Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
